import React from 'react'
import { X } from 'lucide-react'

interface ValueLogRecord {
  id: string
  zoneId: string
  zoneName: string
  locationId?: string
  locationName?: string
  value: number
  timestamp: number
  formattedTime: string
}

interface WorkerValueSummaryModalProps {
  isOpen: boolean
  workerName: string
  taskName: string
  summaryData: ValueLogRecord[]
  onClose: () => void
}

const WorkerValueSummaryModal: React.FC<WorkerValueSummaryModalProps> = ({
  isOpen,
  workerName,
  taskName,
  summaryData,
  onClose,
}) => {
  if (!isOpen) return null

  const formatNumber = (num: number): string => {
    return num.toFixed(2).replace(/\.?0+$/, '')
  }

  const totalValue = summaryData.reduce((sum, item) => sum + item.value, 0)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{workerName}</h2>
            <p className="text-sm text-gray-600">{taskName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {summaryData.length > 0 ? (
            <div className="space-y-1">
              {summaryData.map((record) => (
                <div
                  key={record.id}
                  className="flex items-center justify-between text-sm py-1"
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-gray-900">
                      {record.zoneName}
                      {record.locationName && `:${record.locationName}`}
                    </span>
                    <span className="text-gray-600 ml-2">{record.formattedTime}</span>
                  </div>
                  <div className="text-right ml-2 flex-shrink-0">
                    <span className="font-semibold text-gray-900">
                      {formatNumber(record.value)}
                    </span>
                  </div>
                </div>
              ))}

              {/* Total */}
              <div className="border-t border-gray-300 pt-2 mt-3">
                <div className="flex items-center justify-between font-semibold">
                  <span className="text-gray-900">Total</span>
                  <span className="text-blue-600">
                    {formatNumber(totalValue)}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-600">No records found</p>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default WorkerValueSummaryModal
